//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

// Polyfills for Array methods added in ES2023.  Import this file once in
// main.jsx for side effects.  After that, all arrays have these methods
// available natively throughout the app.
//
// Remove this file when the project transitions to lib: ES2023 or higher.

declare global {
  interface Array<T> {
    findLastIndex(
      predicate: (value: T, index: number, obj: T[]) => boolean
    ): number;
    findLast(
      predicate: (value: T, index: number, obj: T[]) => boolean
    ): T | undefined;
    toSorted(compareFn?: (a: T, b: T) => number): T[];
    toReversed(): T[];
    toSpliced(start: number, deleteCount?: number, ...items: T[]): T[];
    with(index: number, value: T): T[];
  }
}

if (!Array.prototype.findLastIndex) {
  Array.prototype.findLastIndex = function(predicate) {
    for (let i = this.length - 1; i >= 0; i--) {
      if (predicate(this[i], i, this))
        return i;
    }
    return -1;
  };
}

if (!Array.prototype.findLast) {
  Array.prototype.findLast = function(predicate) {
    for (let i = this.length - 1; i >= 0; i--) {
      if (predicate(this[i], i, this))
        return this[i];
    }
    return undefined;
  };
}

if (!Array.prototype.toSorted) {
  Array.prototype.toSorted = function(compareFn) {
    return [...this].sort(compareFn);
  };
}

if (!Array.prototype.toReversed) {
  Array.prototype.toReversed = function() {
    return [...this].reverse();
  };
}

if (!Array.prototype.toSpliced) {
  Array.prototype.toSpliced = function(start, deleteCount, ...items) {
    const copy = [...this];
    if (deleteCount === undefined) {
      copy.splice(start);
    } else {
      copy.splice(start, deleteCount, ...items);
    }
    return copy;
  };
}

if (!Array.prototype.with) {
  Array.prototype.with = function(index, value) {
    const len = this.length;
    const actualIndex = index < 0 ? len + index : index;
    if (actualIndex < 0 || actualIndex >= len)
      throw new RangeError('Invalid index');
    const copy = [...this];
    copy[actualIndex] = value;
    return copy;
  };
}

export {};
